import OSS from 'ali-oss';
import Dexie from 'dexie';

import { fetchToken } from '@/services';
import { videoTranscode } from '@/services/common/videoTranscode';
import { TIMEOUT } from '@/utils/const';
import { getSafeFileName } from '@/utils/fileUtil';

import {
  ErrorType,
  IRequestTokenData,
  IStorageItem,
  IStoreItem,
  ITokenData,
  IUploadDBItem,
  IUploadOptions,
  TaskStatus,
  UploadType,
} from '../type';
import { Queue } from './helper/QueueHelper';
import { StorageHelper } from './helper/StorageHelper';
import { StoreHelper } from './helper/StoreHelper';
import { AwsUploader } from './Uploader/AwsUploader';
import { CosUploader } from './Uploader/CosUploader';
import { OssUploader } from './Uploader/OssUploader';

const cancelSet: Set<string> = new Set([]);
const failSet: Set<string> = new Set([]);

// 存储在localStorage的键值
const UPLOADER_STORAGE_KEY = 'UploaderInfo';

// 实例化操作队列
const queue = Queue.getInstance();

// 断点续传
export class Uploader<SHelper extends StorageHelper<Dexie>> {
  private OssHelper: OssUploader;
  private AwsHelper: AwsUploader;
  private CosHelper: CosUploader;
  constructor(
    private storageInstance: SHelper | null,
    private resume: boolean = false,
    timeout: number = TIMEOUT,
  ) {
    this.OssHelper = new OssUploader(timeout);
    this.AwsHelper = new AwsUploader(timeout);
    this.CosHelper = new CosUploader(timeout);
  }

  // 获取实例
  static async getInstance(resume = false, timeout = TIMEOUT) {
    const storageInstance = resume ? await StorageHelper.getInstance() : null;
    return new Uploader(storageInstance, resume, timeout);
  }

  // 入口-启动
  public async upload(data: IUploadOptions) {
    const { files, workspace, dirSceneType: CommonSceneType } = data || {};

    if (files && files.length) {
      for await (let fileInfo of files) {
        const {
          file,
          id,
          needTransCode = false,
          needTransfer = false,
          dirSceneType: fileSceneType,
        } = fileInfo;
        // dirSceneType: 默认获取单个file对象内部的dirSceneType，没有就取外面公共的
        const dirSceneType = fileSceneType || CommonSceneType;
        const fileName = getSafeFileName(file.name);
        const start = new Date().getTime();
        const tokenData = await this.getTokenData({
          fileName,
          dirSceneType,
        });

        if (!tokenData) {
          const end = new Date().getTime();
          const error = new Error('获取Token Data失败');
          error.name = ErrorType.Other;
          const errorStoreInfo: IStoreItem = {
            fileName,
            requestTokenData: {
              dirSceneType,
              fileName: file.name,
            },
            progressData: {
              status: TaskStatus.FAILED,
              percent: 0,
            },
            tokenData: {
              accessKey: '',
              accessSecret: '',
              securityToken: '',
              bucket: '',
              accessUrl: '',
              region: '',
              filePath: '',
              ossType: UploadType.OSS,
              uploadHost: '',
            },
            uploadId: '',
            client: null,
            file,
            errorInfo: {
              name: error.name,
              message: error.message,
              error,
              start,
              end,
            },
            completeInfo: {
              start,
              end,
            },
          };
          Uploader.updateStatus(workspace, id, errorStoreInfo);
          continue;
        }
        const { ossType } = tokenData || {};

        // 接口返回的是什么类型就用什么上传
        const uploadHelper =
          ossType === UploadType.AWS
            ? this.AwsHelper
            : ossType === UploadType.OSS
              ? this.OssHelper
              : this.CosHelper;
        // 首次上传获取uploadId等信息
        const { uploadId, client } = await uploadHelper.init(tokenData);

        if (uploadId) {
          // 初始化store信息
          const storeInfo: IStoreItem = {
            fileName,
            requestTokenData: {
              dirSceneType,
              fileName: file.name,
            },
            progressData: {
              status: TaskStatus.UPLOADING,
              percent: 0,
            },
            tokenData,
            uploadId,
            client,
            file,
          };
          Uploader.updateStatus(workspace, id, storeInfo);
          // 加入任务队列
          queue.add(workspace, {
            id,
            task: async () => {
              return await this.startUpload(
                id,
                workspace,
                storeInfo,
                needTransCode,
                needTransfer,
              );
            },
          });
        }
      }
      queue.post(workspace);
    }
  }

  private async getTokenData(params: IRequestTokenData): Promise<ITokenData | null> {
    try {
      const { fileName, dirSceneType } = params;
      const data = await fetchToken(fileName, dirSceneType);
      if (data.code !== 200 || !data.data) return null;
      return data.data;
    } catch (error) {
      return null;
    }
  }

  // 执行上传任务
  private async startUpload(
    fileId: string,
    workspace: string,
    data: IStoreItem,
    // 是否支持转码
    needTransCode: boolean,
    needTransfer: boolean,
  ) {
    const {
      requestTokenData,
      tokenData,
      client,
      uploadId,
      fileName,
      file,
      progressData,
    } = data || {};

    const { filePath, ossType } = tokenData;
    if (client) {
      const startTime = new Date().getTime();
      try {
        // 是否开启断点续传
        if (this.resume) {
          await this.insertDB(uploadId, workspace, file);
          this.updateStorage(workspace, fileId, {
            requestTokenData,
            tokenData,
            uploadId,
            fileName,
            progressData: {
              ...progressData,
              needTransCode,
              needTransfer,
            },
          });
        }
        let that = this;
        const helper =
          ossType === UploadType.AWS
            ? this.AwsHelper
            : ossType === UploadType.OSS
              ? this.OssHelper
              : this.CosHelper;
        let first = true;
        // 执行上传
        const res: any = await helper.upload({
          uploadId,
          client,
          filePath,
          file,
          tokenData,
          options: {
            async progress(percent: number, checkPoint: any) {
              // 已经取消的或者失败的任务不做处理
              if (cancelSet.has(uploadId) || failSet.has(uploadId)) return;
              let completeInfo;
              if (percent === 1) {
                completeInfo = {
                  start: startTime,
                  end: new Date().getTime(),
                };
              }
              // 如果需要转码服务，在上传成功后将转码后的视频链接返回

              const storageInfo: IStorageItem = {
                fileName,
                tokenData,
                requestTokenData,
                progressData: {
                  checkPoint,
                  status: percent === 1 ? TaskStatus.SUCCESS : TaskStatus.UPLOADING,
                  percent,
                  isStart: first,
                  needTransCode,
                  needTransfer,
                },
                uploadId,
                completeInfo,
              };
              if (first) first = false;

              if (percent === 1 && needTransCode) {
                storageInfo.progressData.status = TaskStatus.TRANSCODING;
                // 更新状态
                Uploader.updateStatus(workspace, fileId, {
                  ...storageInfo,
                  client,
                  file,
                });
                const success = await that.transCode(storageInfo);
                if (!success) {
                  const err = new Error('Upload Transcoding Failed');
                  err.name = ErrorType.TranscodingError;
                  throw err;
                }
                if (cancelSet.has(uploadId) || failSet.has(uploadId)) return;
              }
              const storeInfo: IStoreItem = {
                ...storageInfo,
                client,
                file,
              };
              if (that.resume) {
                // 更新storage
                that.updateStorage(workspace, fileId, storageInfo);
              }

              // 更新状态
              Uploader.updateStatus(workspace, fileId, storeInfo);
            },
          },
        });
        if (!res && ossType !== UploadType.COS) {
          const err = new Error('Upload Failed');
          err.name = ErrorType.Other;
          throw err;
        }
      } catch (error: any) {
        const endTime = new Date().getTime();
        failSet.add(uploadId);
        const err = new Error(error?.message || '');
        err.name = error?.name || ErrorType.Other;
        Uploader.updateStatus(workspace, fileId, {
          progressData: {
            status:
              err.name === ErrorType.TranscodingError
                ? TaskStatus.TRANSCODING_FAILED
                : TaskStatus.FAILED,
          },
          fileName,
          errorInfo: {
            name: err.name,
            message: err.message,
            error: err,
            start: startTime,
            end: endTime,
          },
          completeInfo: {
            start: startTime,
            end: endTime,
          },
        });
      }
    }
  }

  // 重新上传
  public async reUpload(workspace: string, fileId: string, info: IStoreItem) {
    const {
      client,
      file,
      requestTokenData,
      progressData,
      uploadId,
      tokenData,
      fileName,
    } = info;
    const { checkPoint, needTransCode, needTransfer } = progressData;
    const { ossType } = tokenData;

    let checkpoint = checkPoint;
    const that = this;
    const uploadHelper =
      ossType === UploadType.AWS
        ? this.AwsHelper
        : ossType === UploadType.OSS
          ? this.OssHelper
          : this.CosHelper;
    const startTime = new Date().getTime();
    let first = true;
    try {
      const res = await uploadHelper.retry({
        info,
        options: {
          async progress(percent: number, checkPoint: OSS.Checkpoint) {
            // 已经取消的任务不做处理
            if (cancelSet.has(uploadId)) return;
            checkpoint = checkPoint;
            let completeInfo;
            if (percent === 1) {
              completeInfo = {
                start: startTime,
                end: new Date().getTime(),
              };
            }
            const storageInfo: IStorageItem = {
              progressData: {
                checkPoint,
                status: percent === 1 ? TaskStatus.SUCCESS : TaskStatus.UPLOADING,
                percent,
                isStart: first,
                needTransfer,
              },
              tokenData,
              requestTokenData,
              fileName,
              uploadId,
              completeInfo,
            };
            if (first) first = false;
            // 做视频转码
            if (percent === 1 && needTransCode) {
              storageInfo.progressData.status = TaskStatus.TRANSCODING;
              // 更新状态
              Uploader.updateStatus(workspace, fileId, {
                ...storageInfo,
                client,
                file,
              });
              const success = await that.transCode(storageInfo);
              if (!success) {
                const err = new Error('Upload Transcoding Failed');
                err.name = ErrorType.TranscodingError;
                throw err;
              }
              if (cancelSet.has(uploadId)) return;
            }
            if (that.resume) {
              // 更新storage
              that.updateStorage(workspace, fileId, storageInfo);
            }

            const storeInfo: IStoreItem = {
              ...storageInfo,
              client,
              file,
            };

            // 更新状态
            Uploader.updateStatus(workspace, fileId, storeInfo);
          },
          checkpoint,
          headers: { 'cache-control': '', 'Content-Disposition': 'attachment' },
        },
      });
      if (!res) {
        const err = new Error('Upload Failed');
        err.name = ErrorType.Other;
        throw err;
      }
    } catch (error: any) {
      const endTime = new Date().getTime();
      failSet.add(uploadId);
      const err = new Error(error?.message || '');
      err.name = error?.name || ErrorType.Other;
      Uploader.updateStatus(workspace, fileId, {
        progressData: {
          status:
            err.name === ErrorType.TranscodingError
              ? TaskStatus.TRANSCODING_FAILED
              : TaskStatus.FAILED,
        },
        fileName,
        errorInfo: {
          name: err.name,
          message: err.message,
          error: err,
          start: startTime,
          end: endTime,
        },
        completeInfo: {
          start: startTime,
          end: endTime,
        },
      });
    }
  }

  public async transCode(storageItem: IStorageItem) {
    try {
      const { fn, handler } = videoTranscode();
      storageItem.progressData.transCodeHandler = handler;
      const info = await fn(
        storageItem.tokenData.accessUrl,
        storageItem.progressData.needTransfer,
      );
      storageItem.progressData.status = TaskStatus.SUCCESS;
      if (info.videoUrl) {
        storageItem.tokenData.accessUrl = info.videoUrl;
        storageItem.tokenData.coverUrl = info.coverUrl;
      }
      return true;
    } catch (error) {
      console.error(error);
      storageItem.progressData.status = TaskStatus.TRANSCODING_FAILED;
      return false;
    }
  }

  // 取消上传任务
  public async cancel(fileId: string, workspace: string) {
    const { uploaderInfo } = StoreHelper.getStoreInfo();
    const fileInfo = uploaderInfo?.[workspace]?.[fileId];
    const { uploadId, tokenData } = fileInfo;
    const { ossType } = tokenData;
    try {
      queue.delete(workspace, fileId);
      cancelSet.add(uploadId);
      const uploadHelper =
        ossType === UploadType.AWS
          ? this.AwsHelper
          : ossType === UploadType.OSS
            ? this.OssHelper
            : this.CosHelper;
      await this.delete(fileId, workspace);
      await uploadHelper.cancel(fileInfo);
      return true;
    } catch (error) {
      return false;
    }
  }

  // 删除上传任务
  public async delete(fileId: string, workspace: string) {
    const { uploaderInfo } = StoreHelper.getStoreInfo();
    const fileInfo = uploaderInfo?.[workspace]?.[fileId];
    try {
      // 开启断点续传的上传，在删除时，需要删除storage和IndexDB
      if (this.resume) {
        const dbRes = await this.deleteDBItem(fileInfo.uploadId, workspace);
        if (!dbRes) throw new Error();
        const storageRes = this.deleteStorageItem(fileId, workspace);
        if (!storageRes) throw new Error();
      }

      const { uploaderInfo } = StoreHelper.getStoreInfo();
      if (uploaderInfo?.[workspace]?.[fileId]) {
        delete uploaderInfo?.[workspace]?.[fileId];
      }
      Uploader.setUpdateStatus(Object.assign({}, uploaderInfo));
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  public async deleteAll(workspace: string) {
    try {
      const { uploaderInfo } = StoreHelper.getStoreInfo();
      if (uploaderInfo?.[workspace]) {
        delete uploaderInfo?.[workspace];
      }
      Uploader.setUpdateStatus(Object.assign({}, uploaderInfo));
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  // 重新上传任务
  public async retry(fileId: string, workspace: string) {
    const { uploaderInfo } = StoreHelper.getStoreInfo();
    const info = uploaderInfo?.[workspace]?.[fileId];
    if (info) {
      console.log(info.progressData.status);
      if (info.progressData.status === TaskStatus.TRANSCODING_FAILED) {
        const { client, file, fileName, requestTokenData, uploadId, completeInfo } = info;
        Uploader.updateStatus(workspace, fileId, {
          progressData: {
            status: TaskStatus.TRANSCODING,
            percent: 1,
          },
          fileName: info.fileName,
        });
        let status = TaskStatus.SUCCESS;
        const startTime = new Date().getTime();
        const success = await this.transCode(info);

        let errorInfo;
        if (!success) {
          status = TaskStatus.TRANSCODING_FAILED;
          const err = new Error('Upload Transcoding Failed');
          err.name = ErrorType.TranscodingError;
          errorInfo = {
            name: err.name,
            message: err.message,
            error: err,
            start: startTime,
            end: new Date().getTime(),
          };
        }

        const storageInfo: IStorageItem = {
          fileName,
          tokenData: info.tokenData,
          requestTokenData,
          progressData: {
            status,
            percent: success ? 1 : 0,
          },
          errorInfo,
          uploadId,
          completeInfo,
        };
        if (this.resume) {
          // 更新storage
          this.updateStorage(workspace, fileId, storageInfo);
        }

        const storeInfo: IStoreItem = {
          ...storageInfo,
          client,
          file,
        };

        // 更新状态
        Uploader.updateStatus(workspace, fileId, storeInfo);
      } else {
        Uploader.updateStatus(workspace, fileId, {
          progressData: {
            status: TaskStatus.UPLOADING,
            percent: 0,
          },
          fileName: info.fileName,
        });
        return await this.reUpload(workspace, fileId, info);
      }
    }
  }

  // 根据workspace更新checkpoint的信息
  private async getUploadInfoByWorkspace(
    parser: { [propname: string]: { [propname: string]: IStorageItem } },
    workspace: string,
  ) {
    const workspaceItem = parser[workspace];
    if (workspaceItem) {
      const dbInfo = await this.getWorkspaceDbInfo(workspace);
      for (let fileId in workspaceItem) {
        const uploadId = workspaceItem[fileId].uploadId;
        const dbFileInfo = dbInfo[uploadId] || {};
        const file = dbFileInfo?.file;
        const item = workspaceItem[fileId];
        if (item && file) {
          const { progressData } = item;
          if (progressData.checkPoint) {
            try {
              const checkPoint = JSON.parse(String(progressData.checkPoint));
              checkPoint.file = file;
              progressData.checkPoint = checkPoint;
            } catch (error) {
              console.error(error);
            }
          }
        }
      }
      return {
        [workspace]: workspaceItem,
      };
    }
    return {};
  }

  // 删除localStorage item
  private deleteStorageItem(fileId: string, workspace: string) {
    const storage = this.storageInstance;
    if (!storage) return;
    const parser = this.getParser();
    try {
      if (parser?.[workspace]?.[fileId]) {
        delete parser[workspace][fileId];
        storage.setStorageItem(UPLOADER_STORAGE_KEY, parser);
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  // 更新localStorage中的upload信息
  private updateStorage(
    workspace: string,
    fileId: string,
    params: Partial<IStorageItem>,
  ) {
    const storage = this.storageInstance;
    if (!storage) return;
    const item = this.getParser();
    try {
      const parser = item || {};
      if (!parser[workspace]) parser[workspace] = {};
      if (!parser[workspace][fileId]) {
        parser[workspace][fileId] = {
          status: TaskStatus.UPLOADING,
        };
      }
      if (params) {
        const progressData = params.progressData;
        parser[workspace][fileId] = Object.assign(parser[workspace][fileId], {
          ...params,
          progressData: {
            ...progressData,
            checkPoint: progressData?.checkPoint
              ? JSON.stringify(progressData?.checkPoint)
              : null,
          },
        });
      }

      storage.setStorageItem(UPLOADER_STORAGE_KEY, parser);
    } catch (error) {}
  }

  // 获取localStorage中的upload信息
  private getParser = () => {
    const storage = this.storageInstance;
    if (!storage) return;
    return storage.getStorageItem(UPLOADER_STORAGE_KEY);
  };

  // 向Index DB 插入当前上传的文件信息
  private async insertDB(uploadId: string, workspace: string, file: File) {
    if (!this.storageInstance) return;
    const item: IUploadDBItem = {
      workspace,
      value: {
        [uploadId]: {
          workspace,
          file,
        },
      },
    };
    const dbItem = await this.storageInstance.get(workspace);
    if (dbItem) {
      const oldValue = dbItem.value;
      const newValue = item.value;
      const computedValue = Object.assign(oldValue, newValue);
      item.value = computedValue;
      await this.storageInstance.update(workspace, item);
    } else {
      await this.storageInstance.add(item);
    }
  }

  // 删除Index DB中的某一条上传数据
  private async deleteDBItem(uploadId: string, workspace: string) {
    if (!this.storageInstance) return;
    const dbItem = await this.storageInstance.get(workspace);
    if (dbItem) {
      const value = dbItem.value;
      try {
        if (value[uploadId]) {
          delete value[uploadId];
          await this.storageInstance.update(workspace, dbItem);
        }
      } catch (error) {
        console.error(error);
        return false;
      }
    }
    return true;
  }

  // 根据workspace获取对应IndexDb的信息
  private async getWorkspaceDbInfo(workspace: string) {
    if (!this.storageInstance) return null;
    const info = await this.storageInstance.get(workspace);
    if (info) return info.value;
    return null;
  }

  // 查寻当前所有上传信息
  private async getUploadInfo(
    workspace?: string,
  ): Promise<{ [propname: string]: { [propname: string]: IStorageItem } }> {
    const parser = this.getParser();
    if (parser) {
      if (workspace) {
        return await this.getUploadInfoByWorkspace(parser, workspace);
      } else {
        let info = {};
        for (let workspace in parser) {
          const res = await this.getUploadInfoByWorkspace(parser, workspace);
          info = { ...info, ...res };
        }
        return info;
      }
    }
    return {};
  }

  // 检查当前上传的所有任务并进行断点重启
  public async check() {
    const uploadInfo = await this.getUploadInfo();
    // 更新状态
    Uploader.setUpdateStatus(uploadInfo);
    for (let workspace in uploadInfo) {
      const fileInfo = uploadInfo[workspace];
      if (fileInfo) {
        for (let fileId in fileInfo) {
          const info = fileInfo[fileId];
          const { progressData, requestTokenData } = info;
          if (progressData.status !== TaskStatus.SUCCESS) {
            // 拿一下重启任务需要的参数
            const storeInfo: IStoreItem = {
              ...info,
              file: progressData.checkPoint?.file,
              client: null,
            };
            queue.add(workspace, {
              id: fileId,
              task: async () => {
                return this.reUpload(workspace, fileId, storeInfo);
              },
            });
          }
        }
      }
      queue.post(workspace);
    }
  }

  // 初始化监听
  public init() {
    const fn = (e: any) => {
      const { detail } = e;
      if (detail) {
        StoreHelper.updateStore(detail.workspace, detail.fileId, detail.info);
      }
    };
    window.addEventListener('updateStatus', fn);
    this.check();
    return () => {
      window.removeEventListener('updateStatus', fn);
    };
  }

  // 更新uploadStore中的store信息
  static setUpdateStatus(info: any) {
    StoreHelper.setUploaderInfo(info);
  }

  // 更新状态信息
  static updateStatus(workspace: string, fileId: string, info: Partial<IStoreItem>) {
    window.dispatchEvent(
      new CustomEvent('updateStatus', {
        detail: { workspace, fileId, info },
      }),
    );
  }
}
